import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { MACRO_GOALS } from "../data/meals";
import { todayKey, dateKey } from "../utils/helpers";
import { getCoachBriefing, getDailyPlan, buildWeightContext } from "../lib/coach";
import { getTodayRun } from "../utils/run-plan";

import Day1Mission from "../components/Day1Mission";
import DailyRitual from "../components/DailyRitual";
import MoodMoment from "../components/MoodMoment";
import DailyScoreCard from "../components/DailyScoreCard";
import MoodCheckIn from "../components/MoodCheckIn";
import MoodEvidence from "../components/MoodEvidence";
import CrisisSupport from "../components/CrisisSupport";
import CommitmentCard from "../components/CommitmentCard";
import PastSelfMessage from "../components/PastSelfMessage";
import { shouldSuggestSupport } from "../lib/crisis";
import { completeCommitment } from "../utils/commitment";
import { pairSessionsWithDailyFallback } from "../utils/mood";
import { ritualGreeting } from "../utils/ritual";
import TodayRunCard from "../components/TodayRunCard";
import TodayWorkoutCard from "../components/TodayWorkoutCard";
import WorkoutGenerationForm from "../components/WorkoutGenerationForm";
import TodayPlanSection from "../components/TodayPlanSection";
import WeeklyDotTracker from "../components/WeeklyDotTracker";
import RecentActivity from "../components/RecentActivity";
import {
  SkeletonCoachHero,
  SkeletonWorkout,
  SkeletonPlan,
  SkeletonWeekDots,
} from "../components/Skeleton";
import "../styles/home-surface.css";

const ALL_MUSCLE_GROUPS = ["Chest", "Back", "Legs", "Shoulders", "Arms", "Core"];

/**
 * Presentational gate for the coach's line on Home.
 *
 * The coach engine also serves training surfaces, so its vocabulary includes PR
 * counts, muscle-group recency ("Arms hasn't been hit in 9 days") and distance
 * from a goal weight. None of that belongs in the top third of a mental-health
 * home screen: a body-part in arrears is pure loss aversion, and a weight-to-
 * target readout is exactly the disordered-eating liability this product has to
 * stay away from. So the line is rendered only when it is about the person
 * rather than about their performance — otherwise nothing renders at all,
 * which is a perfectly good outcome for this slot.
 *
 * Nothing is computed differently; this filters what is shown, here, only.
 */
const NOT_FOR_THE_WELLNESS_LOOP = [
  /\bPRs?\b/,                                     // "You hit 4 PRs this week"
  /personal record/i,
  /\b(hasn't|has not|haven't) been (hit|trained)/i,
  /\boverdue\b/i,
  /could use attention/i,
  /\bneglect/i,
  /\d+(\.\d+)?\s*(lbs?|kgs?|pounds)\b/i,          // "3.2 lbs from target"
  /\btarget\b/i,
  /\bdeficit\b|\bsurplus\b/i,
  /\bstreak\b/i,                                  // never framed as debt here
  /\bcalorie|\bmacros?\b/i,
  /\b\d+\s*g\b/i,                                 // "18g protein"
  /\bprotein\b/i,
  /\bfuel\b/i,
  /make the next one count/i,
  /stay the course|stay consistent|push for/i,
  /momentum is real/i,
  /don'?t let|slip\b/i,
];

function supportiveLine(text) {
  if (typeof text !== "string" || !text.trim()) return null;
  return NOT_FOR_THE_WELLNESS_LOOP.some((re) => re.test(text)) ? null : text;
}

export default function HomeTab({
  workouts,
  runs,
  todayTotals,
  todayBurn,
  currentStreak,
  macroGoals,
  exercises,
  mealLog,
  personalRecords,
  weightLog,
  onStartWorkout,
  onViewWorkout,
  onViewRun,
  onNavigateToNutrition,
  onLogWeight,
  userProfile,
  allMeals,
  targetWeight,
  weightReminderTime,
  todayAiWorkout,
  aiWorkoutLoading,
  aiWorkoutError,
  onClearAiError,
  onGenerateWorkout,
  onQuickGenerate,
  onBeginAiWorkout,
  onRegenerateWorkout,
  userId,
  moodCheckins = [],
  moodLoaded = false,
  onSaveMood,
  onSaveMoodNote,
  onLogMeal,
  onAddCustomMeal,
  onRemove,
  onUpdateServings,
  onRefresh,
  isRefreshing,
  onShareStreak,
  runPlan,
  onLogRun,
  onViewRunPlan,
}) {
  const workoutSectionRef = useRef(null);
  const [showGenForm, setShowGenForm] = useState(false);
  // Food, macros and the rest of the numbers open on request, not by default.
  const [showTheRest, setShowTheRest] = useState(false);
  const goals = macroGoals || MACRO_GOALS;

  const isLoading = !workouts || !mealLog;

  // ─── Memoized computations ───

  const weeklyData = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      const key = dateKey(d);
      const isToday = i === 6;
      const dayWorkouts = (workouts || []).filter((w) => dateKey(w.startTime) === key);
      const hasWorkout = dayWorkouts.length > 0;
      const dayMeals = (mealLog || {})[key] || [];
      const hasMeal = dayMeals.filter((e) => e.mealId !== "__water__").length > 0;
      const hasRun = (runs || []).some((r) => dateKey(r.timestamp) === key);
      const level = (hasWorkout || hasRun ? 2 : 0) + (hasMeal ? 1 : 0);
      return {
        label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
        shortLabel: ["S", "M", "T", "W", "T", "F", "S"][d.getDay()],
        date: new Date(d),
        isToday,
        hasWorkout: hasWorkout || hasRun,
        hasMeal,
        level,
      };
    });
  }, [workouts, mealLog, runs]);

  const weekActiveDays = useMemo(
    () => weeklyData.filter((d) => d.level > 0).length,
    [weeklyData]
  );

  const { recentMuscles, suggestedFocus } = useMemo(() => {
    const twoDaysAgo = Date.now() - 2 * 86400000;
    const recent = new Set();
    for (const w of (workouts || []).filter((w) => w.startTime >= twoDaysAgo)) {
      for (const b of w.exerciseBlocks || []) {
        const ex = (exercises || []).find((e) => e.id === b.exerciseId);
        if (ex?.category) recent.add(ex.category);
      }
    }

    const muscleRecency = {};
    for (const cat of ALL_MUSCLE_GROUPS) {
      for (const w of [...(workouts || [])].sort((a, b) => b.startTime - a.startTime)) {
        const hasCat = (w.exerciseBlocks || []).some((b) => {
          const ex = (exercises || []).find((e) => e.id === b.exerciseId);
          return ex && ex.category === cat;
        });
        if (hasCat) {
          muscleRecency[cat] = Math.floor((Date.now() - w.startTime) / 86400000);
          break;
        }
      }
    }

    const suggested =
      ALL_MUSCLE_GROUPS.filter((c) => !recent.has(c)).sort(
        (a, b) => (muscleRecency[b] ?? 999) - (muscleRecency[a] ?? 999)
      )[0] ||
      ALL_MUSCLE_GROUPS.find((c) => !recent.has(c)) ||
      "Chest";

    return { recentMuscles: recent, suggestedFocus: suggested };
  }, [workouts, exercises]);

  const latestWeight = (weightLog || [])[0]?.weight ?? null;
  const weightCtx = useMemo(
    () => buildWeightContext(targetWeight, latestWeight, weightLog),
    [targetWeight, latestWeight, weightLog]
  );

  const profileWithCtx = useMemo(
    () => (userProfile ? { ...userProfile, _weightCtx: weightCtx } : userProfile),
    [userProfile, weightCtx]
  );

  const coaching = useMemo(
    () =>
      getCoachBriefing({
        workouts: workouts || [],
        runs: runs || [],
        mealLog: mealLog || {},
        personalRecords: personalRecords || {},
        weightLog: weightLog || [],
        macroGoals: goals,
        exercises: exercises || [],
        todayTotals,
        currentStreak,
        targetWeight,
        weightReminderTime,
        userProfile: profileWithCtx,
        runPlan,
      }),
    [workouts, runs, mealLog, personalRecords, weightLog, goals, exercises, todayTotals, currentStreak, targetWeight, weightReminderTime, profileWithCtx, runPlan]
  );

  const { todayStatus } = coaching;
  const workout = todayAiWorkout?.workout_json;

  // ─── Run plan awareness ───
  const todayScheduledRun = useMemo(
    () => (runPlan ? getTodayRun(runPlan) : null),
    [runPlan]
  );
  const isRunDay = todayScheduledRun && todayScheduledRun.type !== "Rest" && todayScheduledRun.type !== "Cross Train";

  // ─── Daily Plan with persistence ───

  const todayMeals = useMemo(
    () => (mealLog ? mealLog[todayKey()] || [] : []),
    [mealLog]
  );
  const today = todayKey();

  const freshPlan = useMemo(
    () =>
      getDailyPlan({
        workouts: workouts || [],
        exercises: exercises || [],
        allMeals: allMeals || [],
        todayTotals,
        todayMeals,
        macroGoals: goals,
        userProfile: profileWithCtx,
        hasTodayWorkout: !!workout || todayStatus.hasTrained,
        mealLog: mealLog || {},
      }),
    [workouts, exercises, allMeals, todayTotals, todayMeals, goals, profileWithCtx, workout, todayStatus.hasTrained, mealLog]
  );

  const planKey = useMemo(() => `elevate_plan_${today}`, [today]);
  const [savedPlan, setSavedPlan] = useState(() => {
    try {
      const stored = localStorage.getItem(`elevate_plan_${todayKey()}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const planApproved = savedPlan != null && (savedPlan.meals || []).length >= 3;
  const dailyPlan = useMemo(() => {
    if (!planApproved) return freshPlan;
    return {
      ...freshPlan,
      mealPlan: ["Breakfast", "Lunch", "Dinner"]
        .map((slotName) => {
          const saved = (savedPlan.meals || []).find((m) => m.slot === slotName);
          const freshSlot = freshPlan.mealPlan.find((fp) => fp.slot === slotName);
          if (freshSlot?.logged) return freshSlot;
          if (saved) {
            const meal = (allMeals || []).find((m) => m.id === saved.mealId);
            if (meal) return { slot: slotName, meal, logged: false };
          }
          return freshSlot || null;
        })
        .filter(Boolean),
    };
  }, [planApproved, freshPlan, savedPlan, allMeals]);

  const handleSavePlan = useCallback(
    (updated) => {
      localStorage.setItem(planKey, JSON.stringify(updated));
      setSavedPlan(updated);
    },
    [planKey]
  );

  const handleResetPlan = useCallback(() => {
    localStorage.removeItem(planKey);
    setSavedPlan(null);
  }, [planKey]);

  // ─── Quick-repeat workout ───
  const handleRepeatWorkout = useCallback(
    (w) => {
      const musclesUsed = new Set();
      for (const b of w.exerciseBlocks || []) {
        const ex = (exercises || []).find((e) => e.id === b.exerciseId);
        if (ex?.category) musclesUsed.add(ex.category);
      }
      const dur = w.endTime ? Math.round((w.endTime - w.startTime) / 60000) : 30;
      const roundedDur = [20, 30, 45, 60].reduce((best, t) =>
        Math.abs(t - dur) < Math.abs(best - dur) ? t : best
      );
      const focus = [...musclesUsed][0] || "Chest";
      onGenerateWorkout({
        available_time: roundedDur,
        energy_level: 3,
        muscle_focus: focus,
        recent_muscles: [...recentMuscles],
        limitations_today: undefined,
      });
    },
    [exercises, recentMuscles, onGenerateWorkout]
  );

  // ─── Pull-to-refresh ───
  const touchStartY = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const PULL_THRESHOLD = 80;

  const handleTouchStart = useCallback((e) => {
    if (window.scrollY <= 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (window.scrollY > 0 || touchStartY.current === 0) return;
    const distance = Math.max(0, e.touches[0].clientY - touchStartY.current);
    setPullDistance(Math.min(distance * 0.4, 120));
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && onRefresh && !isRefreshing) {
      onRefresh();
    }
    setPullDistance(0);
    touchStartY.current = 0;
  }, [pullDistance, onRefresh, isRefreshing]);

  // Days since last training activity — drives the comeback flow (3+ days)
  const lastActiveTs = Math.max(
    ...(workouts || []).map((w) => w.startTime || 0),
    ...(runs || []).map((r) => r.timestamp || 0),
    0
  );
  const daysAway = lastActiveTs > 0 ? Math.floor((Date.now() - lastActiveTs) / 86400000) : 0;
  const hasAnyActivity = (workouts || []).length > 0 || (runs || []).length > 0;

  // Day 1 mission — only for truly new users (no activity at all), and respects its own dismiss
  const isNewUser = (workouts || []).length === 0 && !Object.values(mealLog || {}).some((entries) => entries.length > 0);
  const day1Dismissed = typeof window !== "undefined" && localStorage.getItem("day1_dismissed") === "1";
  const showDay1Mission = isNewUser && !day1Dismissed;

  const handleGenerate = useCallback(
    (context) => {
      onGenerateWorkout(context);
      setShowGenForm(false);
    },
    [onGenerateWorkout]
  );

  // ─── Smart CTA logic ───
  const scrollToWorkout = useCallback(() => {
    // One tap: generate immediately with smart defaults (no form), then
    // scroll to the loading/result card.
    if (!workout && !aiWorkoutLoading) onQuickGenerate?.();
    requestAnimationFrame(() => {
      workoutSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [workout, aiWorkoutLoading, onQuickGenerate]);

  // ─── The daily ritual ───────────────────────────────────────────────────
  // Home is a sequence before it is a dashboard. Until today has actually been
  // said out loud, nothing further down this page is worth reading — the coach
  // line, the prescription and the evidence all take how you are as their
  // input, so showing them first is showing them empty.
  //
  // The ritual is LATCHED rather than derived: it writes the check-in halfway
  // through (see DailyRitual), which would otherwise make its own opening
  // condition false and unmount it mid-flow. It closes only when the person
  // closes it, and a close is remembered for the rest of the day so the app
  // never asks twice.
  const todayCheckin = useMemo(
    () => moodCheckins.find((c) => c.context === "daily" && c.date === today),
    [moodCheckins, today]
  );

  // How much of a record exists, so the close can state where they actually
  // are instead of leaving two thirds of the screen empty.
  const pairedSoFar = useMemo(
    () => pairSessionsWithDailyFallback(moodCheckins).length,
    [moodCheckins]
  );

  const [ritualOpen, setRitualOpen] = useState(false);
  const [ritualDismissed, setRitualDismissed] = useState(() => {
    try {
      return localStorage.getItem(`ritual_closed_${todayKey()}`) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isLoading || !moodLoaded || !onSaveMood) return;
    if (ritualDismissed || todayCheckin) return;
    setRitualOpen(true);
  }, [isLoading, moodLoaded, onSaveMood, ritualDismissed, todayCheckin]);

  // ─── The close ──────────────────────────────────────────────────────────
  // Marking a commitment done opens the after-reading full-screen. This is the
  // half of the loop that was missing: without it a patient who walked around
  // the block generated no "after", and the evidence card could never fill in
  // for precisely the people it exists for.
  //
  // The reading is written as a post_session check-in against a synthetic id
  // for today's commitment, which is exactly the shape
  // pairSessionsWithDailyFallback already pairs against the morning daily. No
  // change to the evidence engine — it was always able to read this, nothing
  // was ever writing it.
  // { commitment, mode } — "before" as they set off, "after" as they finish.
  const [moment, setMoment] = useState(null);
  const [commitmentEpoch, setCommitmentEpoch] = useState(0);

  /**
   * Both ends of the activation write against the same synthetic id for today's
   * commitment, which makes them a strict pair — and pairSessions() prefers a
   * real pre_session over the morning fallback whenever one exists, so taking
   * the "before" reading automatically upgrades the comparison from
   * "eleven hours of ordinary life" to "the thing they just did".
   */
  const saveReading = useCallback(
    async (mood) => {
      const before = moment?.mode === "before";
      await onSaveMood?.({
        mood,
        context: before ? "pre_session" : "post_session",
        workoutId: `commit_${today}`,
      });
      if (!before) completeCommitment(userId, today);
    },
    [onSaveMood, today, userId, moment]
  );

  const finishMoment = useCallback(() => {
    // Completing on the skip path too, but only for the "after" moment: they
    // did the thing, they just didn't want to be asked about it. Skipping the
    // "before" must never mark anything done.
    if (moment?.mode === "after") completeCommitment(userId, today);
    // After the before-reading, go straight into a session. The reading is the
    // baseline; the workout is the thing being measured against it.
    const startSession = moment?.mode === "before";
    setMoment(null);
    setCommitmentEpoch((n) => n + 1);
    if (startSession) onQuickGenerate?.();
  }, [userId, today, moment, onQuickGenerate]);

  const closeRitual = useCallback(() => {
    try {
      localStorage.setItem(`ritual_closed_${today}`, "1");
    } catch { /* private mode — the in-memory flag still holds for this session */ }
    setRitualDismissed(true);
    setRitualOpen(false);
  }, [today]);

  // ─── Greeting ───
  // The page opens the way a person would be greeted, not the way a dashboard
  // opens. Date small and first, greeting large and second.
  const now = new Date();
  // Same function the ritual opens with, so the two never disagree about what
  // time of day it is (at 1am they used to say "You're up late" and then
  // "Good morning." one screen apart).
  const greeting = `${ritualGreeting(now)}.`;
  const dateLine = now.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  /**
   * Other ways to record today. Deliberately a row of quiet chips rather than
   * three competing primary buttons — there is one primary action on this
   * screen, and it belongs to the check-in or the commitment above.
   *
   * The row is labelled: a single unlabelled pill floating on the page
   * background between two cards belongs to neither of them and reads as
   * leftover UI. "I moved another way" lives here too — it is the same class of
   * action as "Log a run", and it was the third rung of a link ladder at the
   * bottom of the session card.
   */
  const renderShortcuts = () => {
    const hasTrained = todayStatus.hasTrained;
    const hasTracked = todayStatus.hasTracked;
    if (hasTrained && hasTracked) return null;

    return (
      <div className="hs-shortcut-group">
        <p className="hs-key">Or record it another way</p>
        <div className="hs-shortcuts">
          {!hasTrained && isRunDay && (
            <button type="button" className="ds-chip" onClick={() => onLogRun(todayScheduledRun)}>
              Log today&apos;s run
            </button>
          )}
          {!hasTrained && !isRunDay && (
            <button type="button" className="ds-chip" onClick={() => onLogRun()}>
              Log a run
            </button>
          )}
          {!hasTrained && (
            <button type="button" className="ds-chip" onClick={onStartWorkout}>
              I moved another way
            </button>
          )}
          {!hasTracked && (
            <button type="button" className="ds-chip" onClick={onNavigateToNutrition}>
              Log a meal
            </button>
          )}
        </div>
      </div>
    );
  };

  if (moment) {
    // The baseline for the reveal is the most recent reading that precedes the
    // activation: a pre_session taken as they set off if there is one, and the
    // morning daily otherwise.
    const pre = [...moodCheckins]
      .filter((c) => c.context === "pre_session" && c.workout_id === `commit_${today}`)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    const baseline = pre || todayCheckin;

    return (
      <MoodMoment
        mode={moment.mode}
        what={moment.commitment?.what}
        beforeMood={baseline?.mood ?? null}
        beforeAt={baseline?.created_at ?? null}
        pairedSoFar={pairedSoFar}
        onSave={saveReading}
        onDone={finishMoment}
      />
    );
  }

  if (ritualOpen) {
    return (
      <DailyRitual
        userId={userId}
        date={today}
        onSave={onSaveMood}
        onSaveNote={onSaveMoodNote}
        onExit={closeRitual}
        onStart={onQuickGenerate}
        showSupport={shouldSuggestSupport(moodCheckins)}
      />
    );
  }

  return (
    <main
      className="page hs-home"
      id="main-content"
      tabIndex={-1}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="pull-to-refresh"
          style={{
            height: isRefreshing ? 40 : pullDistance,
            opacity: isRefreshing ? 1 : Math.min(1, pullDistance / PULL_THRESHOLD),
          }}
        >
          <div className={`pull-to-refresh-icon${isRefreshing ? " pull-to-refresh-icon--spinning" : ""}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </div>
          {pullDistance >= PULL_THRESHOLD && !isRefreshing && (
            <div className="text-xs" style={{ color: "var(--text3)", marginTop: 4 }}>
              Release to refresh
            </div>
          )}
        </div>
      )}

      {showDay1Mission && <Day1Mission workouts={workouts || []} mealLog={mealLog || {}} />}

      <header className="hs-greeting">
        <p className="ds-meta hs-greeting-date">{dateLine}</p>
        <h1 className="hs-greeting-title">{greeting}</h1>
        {/* A ritual you can leave and never re-enter is a ritual most people
            see exactly once. "Later" set a flag for the rest of the day and
            there was no way back to the screen the product is built on. */}
        {todayCheckin && (
          <button
            type="button"
            className="hs-ritual-again"
            onClick={() => setRitualOpen(true)}
          >
            Check in again
          </button>
        )}
      </header>

      {/* ══ 1. How you are today ═══════════════════════════════════════════
          The hero of the screen. Until it's answered, nothing else on this
          page is more important; once it is, the commitment takes over. */}
      <section className="hs-section" aria-label="How you are today">
        {isLoading && <SkeletonCoachHero />}

        {!isLoading && onSaveMood && (() => {
          return (
            <>
              {shouldSuggestSupport(moodCheckins) && <CrisisSupport variant="sustained" />}
              {/* One way to check in. Before today's reading exists this is a
                  single card that opens the ritual; the old inline form sat
                  directly under a link to the ritual, so the screen offered
                  two different check-ins one above the other. Once answered,
                  MoodCheckIn reads the day back as it always did. */}
              {todayCheckin ? (
                <MoodCheckIn
                  key={todayCheckin.id}
                  existing={todayCheckin}
                  onSave={onSaveMood}
                />
              ) : (
                <button
                  type="button"
                  className="card card--hero hs-start-checkin"
                  onClick={() => setRitualOpen(true)}
                >
                  <span className="hs-start-q">How are you today?</span>
                  <span className="hs-start-sub">
                    Ten seconds. Today&apos;s session gets sized to the answer.
                  </span>
                  <span className="btn btn-primary btn-full hs-start-btn">Check in</span>
                </button>
              )}
              {/* Activation layer — only once we know how they feel today.
                  Measurement tells them what movement does; these get them
                  out the door on the days that's hard. */}
              {todayCheckin && (
                <>
                  <PastSelfMessage userId={userId} mood={todayCheckin.mood} />
                  {!todayStatus.hasTrained && (
                    <CommitmentCard
                      key={commitmentEpoch}
                      userId={userId}
                      date={today}
                      mood={todayCheckin.mood}
                      energy={todayCheckin.energy}
                      onStart={(c) => setMoment({ commitment: c, mode: "before" })}
                      onDone={(c) => setMoment({ commitment: c, mode: "after" })}
                    />
                  )}
                </>
              )}
            </>
          );
        })()}
      </section>

      {/* ══ 2. The evidence ═════════════════════════════════════════════════
          The emotional payoff: their own mood response, in their own data. */}
      {!isLoading && onSaveMood && (
        <div className="hs-section">
          <MoodEvidence
            checkins={moodCheckins}
            workoutDates={[...new Set((workouts || []).map((w) => dateKey(w.startTime)))]}
          />
        </div>
      )}

      {/* ══ 3. Movement ═════════════════════════════════════════════════════ */}
      {isLoading ? (
        <div className="hs-section">
          <SkeletonWorkout />
        </div>
      ) : (
        <section className="hs-section" ref={workoutSectionRef} aria-label="Movement today">
          {/* coaching.nudge is the weight/macro line — it never appears here.
              Weight belongs on the weight surface, behind a deliberate tap. */}
          {supportiveLine(coaching.message) && (
            <div className="card card--quiet">
              <p className="ds-body">{supportiveLine(coaching.message)}</p>
            </div>
          )}

          {runPlan && (
            <TodayRunCard
              plan={runPlan}
              runs={runs}
              onLogRun={onLogRun}
              onViewPlan={onViewRunPlan}
            />
          )}

          {workout || aiWorkoutLoading || aiWorkoutError ? (
            <TodayWorkoutCard
              workout={workout}
              hasTrained={todayStatus.hasTrained}
              aiWorkoutLoading={aiWorkoutLoading}
              aiWorkoutError={aiWorkoutError}
              onClearAiError={onClearAiError}
              onBeginAiWorkout={onBeginAiWorkout}
              onQuickRegenerate={onQuickGenerate}
              onRegenerateWorkout={onRegenerateWorkout}
              onStartWorkout={onStartWorkout}
              onShowGenForm={() => setShowGenForm(true)}
            />
          ) : todayStatus.hasTrained ? (
            <p className="hs-done-note">You moved today. That&apos;s the part that counts.</p>
          ) : showGenForm ? (
            <WorkoutGenerationForm
              recentMuscles={recentMuscles}
              suggestedFocus={suggestedFocus}
              onGenerate={handleGenerate}
              onCancel={() => setShowGenForm(false)}
            />
          ) : (
            /* One tap to a session — time, focus and recovery all come from
               the profile and history already. After a few days away this
               becomes an unhurried welcome back rather than a prompt to
               catch up on anything. */
            <section className="card ds-stack" aria-label="Today's session">
              <div>
                <p className="ds-lead">
                  {daysAway >= 3
                    ? "It's been a few days. Nothing to make up for."
                    : "Nothing planned yet."}
                </p>
                <p className="ds-support" style={{ marginTop: "var(--space-2)" }}>
                  {daysAway >= 3
                    ? "Whenever you're ready, today's session will be a gentler one to come back to."
                    : "Your coach can put something together that fits how today has gone."}
                </p>
              </div>
              <button className="btn btn-primary btn-full" onClick={scrollToWorkout}>
                {daysAway >= 3 ? "Ease back in" : "Put something together"}
              </button>
              <button
                type="button"
                className="ds-link"
                style={{ display: "block", margin: "0 auto" }}
                onClick={() => setShowGenForm(true)}
              >
                I&apos;d rather choose the details
              </button>
            </section>
          )}

          {renderShortcuts()}
        </section>
      )}

      {/* ══ 4. What you actually did ════════════════════════════════════════ */}
      {isLoading ? (
        <div className="hs-section">
          <SkeletonWeekDots />
        </div>
      ) : (
        <div className="hs-section">
          <WeeklyDotTracker
            weeklyData={weeklyData}
            weekActiveDays={weekActiveDays}
            workouts={workouts}
            mealLog={mealLog}
            runs={runs}
          />
          {/* Cumulative, never a debt: a gap in this number is not a loss. */}
          {currentStreak > 0 && (
            <div className="hs-streak">
              <p className="ds-support">
                You&apos;ve shown up {currentStreak} {currentStreak === 1 ? "day" : "days"} in a row.
              </p>
              {currentStreak >= 3 && onShareStreak && (
                <button type="button" className="ds-link" onClick={onShareStreak}>
                  Share
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* No eyebrow, three rows at most, and nothing at all when there is
          nothing to show — an empty-state card here is just more page. */}
      {hasAnyActivity && (
        <div className="hs-section hs-recent">
          <RecentActivity
            workouts={workouts || []}
            runs={runs}
            onViewWorkout={onViewWorkout}
            onViewRun={onViewRun}
            onStartWorkout={onStartWorkout}
            onRepeatWorkout={handleRepeatWorkout}
          />
        </div>
      )}

      {/* ══ 5. Everything else ══════════════════════════════════════════════
          Food, macros and the rest of the numbers, behind one quiet row.
          Nothing has been removed — it is all one tap away — but a wellness
          home screen must not end in a meal plan and a pair of filling bars,
          and the page has to be allowed to stop. */}
      {isLoading ? (
        <div className="hs-section">
          <SkeletonPlan />
        </div>
      ) : (
        <div className="hs-section hs-section--quiet">
          <button
            type="button"
            className="hs-more"
            aria-expanded={showTheRest}
            onClick={() => setShowTheRest((v) => !v)}
          >
            <span>{showTheRest ? "Hide food and the rest of today" : "Food and the rest of today"}</span>
            <span className="hs-more-chev" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>

          {showTheRest && (
            <div className="hs-more-body">
              <TodayPlanSection
                dailyPlan={dailyPlan}
                todayMeals={todayMeals}
                allMeals={allMeals}
                macroGoals={goals}
                todayTotals={todayTotals}
                savedPlan={savedPlan}
                mealLog={mealLog}
                onSavePlan={handleSavePlan}
                onResetPlan={handleResetPlan}
                onLogMeal={onLogMeal}
                onRemove={onRemove}
                onUpdateServings={onUpdateServings}
                onAddCustomMeal={onAddCustomMeal}
              />

              <DailyScoreCard
                workouts={workouts}
                runs={runs}
                mealLog={mealLog}
                todayTotals={todayTotals}
                todayBurn={todayBurn}
                macroGoals={goals}
                weightLog={weightLog}
                onStartWorkout={() => setShowGenForm(true)}
                onNavigateToNutrition={onNavigateToNutrition}
                onLogWeight={onLogWeight}
              />
            </div>
          )}
        </div>
      )}

    </main>
  );
}
